import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { getActiveInactiveUsers } from "firebaseConfig";
import { firestore } from "firebaseConfig";

export const fetchAllProfiles = createAsyncThunk(
  "superAdmin/fetchAllProfiles",
  async (_, thunkAPI) => {
    try {
      const profileRef = collection(firestore, "profile");
      const snapshot = await getDocs(profileRef);

      const profiles = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      return profiles;
    } catch (error) {
      console.error("Error fetching profiles:", error);
      return thunkAPI.rejectWithValue(
        error.message || "Error fetching profiles"
      );
    }
  }
);

export const fetchActiveInactiveUsers = createAsyncThunk(
  "superAdmin/fetchActiveInactiveUsers",
  async (_, thunkAPI) => {
    try {
      const users = await getActiveInactiveUsers();
      return users.data;
    } catch (error) {
      console.error("Error fetching auth users:", error);
      return thunkAPI.rejectWithValue(error.message || "Error fetching users");
    }
  }
);

export const fetchUserProfileByUid = createAsyncThunk(
  "superAdmin/fetchUserProfileByUid",
  async ({ uid }, thunkAPI) => {
    try {
      const ref = doc(firestore, "profile", uid);
      const snapshot = await getDoc(ref);

      if (!snapshot.exists()) {
        throw new Error("User profile not found");
      }

      return { uid, data: snapshot.data() };
    } catch (error) {
      console.error("Error fetching user profile:", error);
      return thunkAPI.rejectWithValue(
        error.message || "Error fetching profile"
      );
    }
  }
);

export const superAdminSlice = createSlice({
  name: "superAdmin",
  initialState: {
    profiles: [],
    selectedUserInfo: {},
    activeUsers: [],
    inactiveUsers: [],
    isFetching: false,
    isSuccess: false,
    isError: false,
    error: "",
  },
  reducers: {
    clearState: (state) => {
      state.isError = false;
      state.isSuccess = false;
      state.isFetching = false;
      state.error = "";
    },
  },
  extraReducers: {
    [fetchAllProfiles.fulfilled]: (state, { payload }) => {
      state.profiles = payload;
      state.isFetching = false;
      state.isSuccess = true;
      state.isError = false;
    },
    [fetchAllProfiles.pending]: (state) => {
      state.isFetching = true;
    },
    [fetchAllProfiles.rejected]: (state, { payload }) => {
      state.profiles = [];
      state.isFetching = false;
      state.isSuccess = false;
      state.isError = true;
      state.error = payload;
    },
    [fetchActiveInactiveUsers.fulfilled]: (state, { payload }) => {
      state.activeUsers = payload.activeUsers;
      state.inactiveUsers = payload.inactiveUsers;
      state.isFetching = false;
      state.isSuccess = true;
      state.isError = false;
    },
    [fetchActiveInactiveUsers.pending]: (state) => {
      state.isFetching = true;
    },
    [fetchActiveInactiveUsers.rejected]: (state, { payload }) => {
      state.activeUsers = [];
      state.inactiveUsers = [];
      state.isFetching = false;
      state.isSuccess = false;
      state.isError = true;
      state.error = payload;
    },
    [fetchUserProfileByUid.fulfilled]: (state, { payload }) => {
      state.selectedUserInfo = payload;
      state.isFetching = false;
      state.isSuccess = true;
      state.isError = false;
    },
    [fetchUserProfileByUid.pending]: (state) => {
      state.isFetching = false;
    },
    [fetchUserProfileByUid.rejected]: (state, { payload }) => {
      state.selectedUserInfo = {};
      state.isFetching = false;
      state.isSuccess = false;
      state.isError = true;
      state.error = payload;
    },
  },
});

export const { clearState } = superAdminSlice.actions;
export const superAdminSelector = (state) => state.superAdmin;
